/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

/**
 * The overview header every editor scope opens with, per the ShopEdit prototype's
 * `.pgd-hdr-box` family: one white bordered box holding a title row — name and meta
 * line on the left, the money on the right — and a field row putting the contractors
 * beside the status rollup.
 */

export const OVERVIEW_HEADER_BOX_STYLE = css`
  label: FOCUSED_OVERVIEW_HEADER;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.stroke};
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const OVERVIEW_HEADER_TITLE_ROW_STYLE = css`
  label: FOCUSED_OVERVIEW_TITLE_ROW;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
`;

export const OVERVIEW_HEADER_TITLE_BLOCK_STYLE = css`
  label: FOCUSED_OVERVIEW_TITLE_BLOCK;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

export const OVERVIEW_HEADER_TITLE_LINE_STYLE = css`
  label: FOCUSED_OVERVIEW_TITLE_LINE;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
`;

/** Display · Medium (Maxeville 24/700) — the scope and group pages' title. */
export const OVERVIEW_HEADER_TITLE_STYLE = css`
  label: FOCUSED_OVERVIEW_TITLE;
  margin: 0;
  font-family: ${FONT.display};
  font-size: ${pxToRem(24)};
  font-weight: 700;
  color: ${COLORS.ink};
  letter-spacing: -0.01em;
  line-height: 1.15;
`;

/** Prototype `.sec-taskcard-amt` — the money, top right, always green. */
export const OVERVIEW_HEADER_AMOUNT_STYLE = css`
  label: FOCUSED_OVERVIEW_AMOUNT;
  flex-shrink: 0;
  font-family: ${FONT.body};
  font-size: ${pxToRem(18)};
  font-weight: 700;
  color: ${COLORS.ink};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  white-space: nowrap;
`;

/** Prototype \`.pgd-label-btn\` — the icon-only trigger that opens label editing. */
export const OVERVIEW_HEADER_LABEL_ICON_BUTTON_STYLE = css`
  label: FOCUSED_OVERVIEW_LABEL_ICON_BUTTON;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 0;
  color: ${COLORS.midtone};
  cursor: pointer;
  transition: color 0.12s, background 0.12s;

  &:hover { color: ${COLORS.ink}; background: ${COLORS.sunken}; }
`;

/** Prototype \`.pgd-label-input\` — replaces the icon button while editing. */
export const OVERVIEW_HEADER_LABEL_INPUT_STYLE = css`
  label: FOCUSED_OVERVIEW_LABEL_INPUT;
  font-family: ${FONT.body};
  font-size: ${pxToRem(14)} !important;
  color: ${COLORS.ink};
  background: ${COLORS.white};
  border: 1px solid ${COLORS.ink};
  padding: 3px 9px;
  outline: none;
  width: 240px;
`;

export const UPDATE_PROGRESS_LABEL = 'Update progress';

/** Prototype `.tup-open` — ink primary on light ground. */
export const OVERVIEW_PROGRESS_UPDATE_BUTTON_STYLE = css`
  label: OVERVIEW_PROGRESS_UPDATE_BUTTON;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9.5)};
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${COLORS.white};
  background: ${COLORS.ink};
  border: 1px solid ${COLORS.ink};
  padding: 7px 13px;
  cursor: pointer;
  white-space: nowrap;
  transition: filter 0.12s;

  &:hover { filter: brightness(1.35); }
  &:focus-visible { outline: 2px solid ${COLORS.ink}; outline-offset: 2px; }
  &:disabled { opacity: 0.45; cursor: not-allowed; filter: none; }
`;

export const OVERVIEW_HEADER_TITLE_ROW_RIGHT_STYLE = css`
  label: OVERVIEW_HEADER_TITLE_ROW_RIGHT;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 14px;
`;
